// packages/frontend/src/stores/enhancedAuth.ts

import { create } from "zustand";
import type { User as FirebaseUser } from "firebase/auth";
import type {
  User,
  Therapist,
  AuthSession,
  AuthCredentials,
} from "@msb/shared";
import { createLogger, logger } from "../utils/logger";
import { auth } from "../services/firebase";
import { subscribeToAuthState, getGlobalAuthState } from "./authContextBridge";
import { analyticsService } from "../services/analyticsService";
import {
  loginWithEmail,
  registerWithEmail,
  logoutUser as firebaseLogout,
  getIdToken,
  loginWithGoogle,
} from "../services/authService";
import {
  cleanupAuthStateListener,
  type StoredUserData,
} from "../services/tokenService";
import { userFetchService } from "../services/user-fetch-service";
import { GENAI_BASE_URL } from "../config/endpoints";
import { healthCheckService } from "../services/healthCheckService";
import { idConsistencyService } from "../services/idConsistencyService";
import { convertFirebaseUserToMSBUser } from "../services/firebaseUserMapping";
import { savePendingProfile } from "../services/pendingProfile";

// Authentication provider types
export type AuthProvider = "jwt" | "firebase";

// Enhanced auth state interface
interface EnhancedAuthState {
  // Core state
  session: AuthSession | null;
  currentUser: User | null;
  currentTherapist: Therapist | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Firebase-specific state
  firebaseUser: FirebaseUser | null;
  storedUserData: StoredUserData | null;
  authProvider: AuthProvider | null;

  // Actions
  login: (
    credentials: AuthCredentials,
    provider?: AuthProvider,
  ) => Promise<boolean>;
  loginWithFirebase: (email: string, password: string) => Promise<boolean>;
  registerWithFirebase: (
    email: string,
    password: string,
    displayName?: string,
    profile?: { prefix?: string; firstName: string; lastName: string },
  ) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  getCurrentTherapist: () => Therapist | null;
  getToken: () => Promise<string | null>;
  initialize: () => Promise<void>;
  switchToFirebase: () => Promise<void>;
  switchToJWT: () => void;
  checkTokenExpiration: () => {
    expired: boolean;
    expiringSoon: boolean;
    minutesRemaining: number;
  };
  isTokenExpiringSoon: () => boolean;
}

// Convert MSB User to Therapist
const convertUserToTherapist = (user: User): Therapist => {
  return {
    ...user,
    userType: "therapist",
    credentials: undefined,
    specializations: undefined,
    licenseNumber: undefined,
  };
};

// Fetch Firebase user from Firestore database
// The user document is created automatically by the onUserCreate Cloud Function trigger
const fetchFirebaseUserFromDatabase = async (
  firebaseUser: FirebaseUser,
): Promise<User | null> => {
  const logger = createLogger("EnhancedAuthStore");

  try {
    logger.info(
      "Fetching user from Firestore (created by onUserCreate trigger)",
      {
        firebaseUid: firebaseUser.uid,
        email: firebaseUser.email || "N/A",
      },
    );

    // Fetch user with retry to handle async delay of onUserCreate trigger
    // The onUserCreate Cloud Function should have already created the user document
    // Use the service defaults (5 attempts with exponential backoff). Right after
    // signup the document is still being created by the onUserCreate trigger, and
    // the previous 3-attempt/1s window regularly expired before it landed.
    const user = await userFetchService.fetchUserWithRetry(firebaseUser.uid);

    if (user) {
      logger.info("User fetched successfully from Firestore", {
        userId: user.id,
        firebaseUid: user.id || firebaseUser.uid,
        email: user.email || "N/A",
        hasFirstName: !!user.firstName,
      });
      return user;
    } else {
      // If user still doesn't exist after retries, fall back to Firebase data
      logger.warn(
        "User not found in Firestore after retries, using Firebase Auth data as fallback",
        {
          firebaseUid: firebaseUser.uid,
          email: firebaseUser.email || "N/A",
        },
      );
      const fallbackUser = convertFirebaseUserToMSBUser(firebaseUser);
      logger.info("Created fallback user from Firebase Auth", {
        userId: fallbackUser.id,
        firstName: fallbackUser.firstName,
        email: fallbackUser.email || "N/A",
      });
      return fallbackUser;
    }
  } catch (error) {
    logger.error("Error fetching user from Firestore", {
      error: error instanceof Error ? error.message : "Unknown error",
      firebaseUid: firebaseUser.uid,
    });
    // Fall back to Firebase Auth data
    return convertFirebaseUserToMSBUser(firebaseUser);
  }
};

// Helper function to perform health check after successful authentication
// This runs completely asynchronously and doesn't block the authentication flow
interface LoggerLike {
  debug: (message: string, data?: unknown) => void;
  info?: (message: string, data?: unknown) => void;
  warn?: (message: string, data?: unknown) => void;
  error?: (message: string, data?: unknown) => void;
}

const performPostLoginHealthCheck = (logger: LoggerLike): void => {
  // Fire and forget - run completely in background (non-blocking)
  (async () => {
    try {
      logger.debug("Starting post-login health check", {
        component: "EnhancedAuthStore",
        services: ["gen-ai-api", "image-storage"],
      });

      // Check both services in parallel (non-blocking)
      const healthResults = await healthCheckService.checkAllServicesHealth();

      // Log results for both services
      if (healthResults.genAiApi.success) {
        logger.debug("Post-login health check successful - gen-ai-api", {
          component: "EnhancedAuthStore",
          service: healthResults.genAiApi.data?.service || "gen-ai-api",
          status: healthResults.genAiApi.data?.status,
          responseTime: healthResults.genAiApi.data?.responseTime,
        });
      } else {
        logger.debug("Post-login health check failed (silent) - gen-ai-api", {
          component: "EnhancedAuthStore",
          error: healthResults.genAiApi.error,
          timestamp: healthResults.genAiApi.timestamp,
        });
      }

      if (healthResults.imageStorage.success) {
        logger.debug("Post-login health check successful - image-storage", {
          component: "EnhancedAuthStore",
          service: healthResults.imageStorage.data?.service || "image-storage",
          status: healthResults.imageStorage.data?.status,
          responseTime: healthResults.imageStorage.data?.responseTime,
        });
      } else {
        logger.debug(
          "Post-login health check failed (silent) - image-storage",
          {
            component: "EnhancedAuthStore",
            error: healthResults.imageStorage.error,
            timestamp: healthResults.imageStorage.timestamp,
          },
        );
      }

      // Silent failure - no user notification, no flow interruption
    } catch (error) {
      logger.debug("Post-login health check error (silent)", {
        component: "EnhancedAuthStore",
        error: error instanceof Error ? error.message : "Unknown error",
      });
      // Silent failure - no user notification, no flow interruption
    }
  })().catch(() => {
    // Extra safety net - completely silent even if promise rejection isn't handled
    // This ensures no unhandled promise rejections
  });
};

// Create the enhanced auth store
// Note: persist() middleware removed - Firebase Auth handles persistence automatically
export const useEnhancedAuthStore = create<EnhancedAuthState>()((set, get) => ({
  // Initial state
  session: null,
  currentUser: null,
  currentTherapist: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,

  // Firebase state
  firebaseUser: null,
  storedUserData: null,
  authProvider: null,

  // Enhanced login method supporting both providers
  login: async (
    credentials: AuthCredentials,
    provider: AuthProvider = "jwt",
  ): Promise<boolean> => {
    const logger = createLogger("EnhancedAuthStore");
    const startTime = Date.now();

    set({ isLoading: true, error: null, authProvider: provider });

    try {
      if (provider === "firebase") {
        const ok = await get().loginWithFirebase(
          credentials.email,
          credentials.password,
        );
        const duration = Date.now() - startTime;
        
        try {
          if (ok) {
            const { firebaseUser } = get();
            const creationTime = new Date(firebaseUser?.metadata.creationTime || '').getTime();
            const lastSignInTime = new Date(firebaseUser?.metadata.lastSignInTime || '').getTime();
            // If creation and last sign in are close, it's likely a new user (or first login)
            const isNewUser = Math.abs(creationTime - lastSignInTime) < 10000;

            analyticsService.trackEvent({
              name: "therapist_login",
              parameters: {
                method: "firebase",
                login_duration_ms: duration,
                user_type: "therapist",
                is_returning_user: !isNewUser,
              },
            });
          } else {
            analyticsService.trackEvent({
              name: "therapist_login_failed",
              parameters: {
                method: "firebase",
                error_code: "unknown",
                user_type: "therapist",
              },
            });
          }
        } catch {}
        return ok;
      } else {
        // JWT authentication (existing logic)
        const apiUrl = GENAI_BASE_URL;

        const response = await fetch(`${apiUrl}/api/v1/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
          }),
        });

        const duration = Date.now() - startTime;

        if (response.ok) {
          const data = await response.json();

          const session: AuthSession = {
            user: data.therapist,
            token: data.token,
            expiresAt: new Date(
              data.expiresAt || Date.now() + 24 * 60 * 60 * 1000,
            ),
          };

          set({
            session,
            currentUser: data.therapist,
            currentTherapist: data.therapist,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Also authenticate with Firebase Auth for Firebase Functions compatibility
          try {
            logger?.info?.("Attempting Firebase Auth for JWT user", {
              component: "EnhancedAuthStore",
            });

            const firebaseResult = await get().loginWithFirebase(
              credentials.email,
              credentials.password,
            );

            logger?.info?.("Firebase Auth result for JWT user", {
              firebaseResult,
              component: "EnhancedAuthStore",
            });
          } catch (firebaseError) {
            logger?.warn?.(
              "Firebase Auth failed for JWT user, continuing with JWT only",
              {
                error: firebaseError,
                component: "EnhancedAuthStore",
              },
            );
            // Continue with JWT authentication even if Firebase Auth fails
          }

          logger?.info?.("User logged in successfully via JWT API", {
            userType: data.therapist.userType,
            component: "EnhancedAuthStore",
          });

          // Perform health check after successful authentication
          performPostLoginHealthCheck(logger);

          try {
            analyticsService.trackEvent({
              name: "therapist_login",
              parameters: {
                method: "jwt",
                login_duration_ms: duration,
                user_type: "therapist",
              },
            });
          } catch {}

          return true;
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData.message || `Authentication failed: ${response.status}`;

          set({
            error: errorMessage,
            isLoading: false,
          });

          logger?.warn?.("JWT API authentication failed", {
            status: response.status,
            error: errorMessage,
            component: "EnhancedAuthStore",
          });

          try {
            analyticsService.trackEvent({
              name: "therapist_login_failed",
              parameters: {
                method: "jwt",
                error_code: response.status.toString(),
                user_type: "therapist",
              },
            });
          } catch {}

          return false;
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Login failed";

      set({
        error: errorMessage,
        isLoading: false,
      });

      logger?.error?.("Login error", {
        error: errorMessage,
        component: "EnhancedAuthStore",
      });

      try {
        analyticsService.trackEvent({
          name: "therapist_login_failed",
          parameters: {
            method: provider,
            error_code: "exception",
            outcome: "error",
            user_type: "therapist",
          },
        });
      } catch {}

      return false;
    }
  },

  // Firebase-specific login
  loginWithFirebase: async (
    email: string,
    password: string,
  ): Promise<boolean> => {
    const logger = createLogger("EnhancedAuthStore");

    set({ isLoading: true, error: null, authProvider: "firebase" });

    try {
      const firebaseUser = await loginWithEmail(email, password);

      // Create fallback user from Firebase Auth data
      const fallbackUser = convertFirebaseUserToMSBUser(firebaseUser);
      const fallbackTherapist = convertUserToTherapist(fallbackUser);

      // Get Firebase token
      const token = await getIdToken();

      // Create initial session with fallback data
      const fallbackSession: AuthSession = {
        user: fallbackUser,
        token: token,
        expiresAt: new Date(Date.now() + 60 * 60 * 1000), // 1 hour
      };

      // Set initial state with fallback data (fast UI response)
      set({
        firebaseUser,
        currentUser: fallbackUser,
        currentTherapist: fallbackTherapist,
        session: fallbackSession,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      logger?.info?.("User logged in successfully via Firebase", {
        userId: firebaseUser.uid,
        component: "EnhancedAuthStore",
      });

      // Fetch real user data from Firestore database (with profile fields)
      // This updates the state asynchronously when complete. An unverified
      // account is refused by the user callables, so it keeps the Auth-based
      // session until the email is verified.
      if (firebaseUser.emailVerified) {
        fetchFirebaseUserFromDatabase(firebaseUser)
          .then((msbUser) => {
            if (msbUser) {
              const therapist = convertUserToTherapist(msbUser);
              const session: AuthSession = {
                user: msbUser,
                token: fallbackSession.token,
                expiresAt: fallbackSession.expiresAt,
              };

              set({
                currentUser: msbUser,
                currentTherapist: therapist,
                session,
              });

              logger?.info?.("User data updated with real Firestore data", {
                userId: firebaseUser.uid,
                component: "EnhancedAuthStore",
              });
            }
          })
          .catch((error) => {
            logger?.warn?.(
              "Failed to fetch user from Firestore, using fallback",
              {
                error: error instanceof Error ? error.message : "Unknown error",
                userId: firebaseUser.uid,
                component: "EnhancedAuthStore",
              },
            );
          });
      }

      // Perform health check after successful authentication
      performPostLoginHealthCheck(logger);

      return true;
    } catch (error) {
      let errorMessage = "Firebase login failed";

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({
        error: errorMessage,
        isLoading: false,
      });

      logger?.error?.("Firebase login failed", {
        error: errorMessage,
        component: "EnhancedAuthStore",
      });

      return false;
    }
  },

  // Firebase registration
  registerWithFirebase: async (
    email: string,
    password: string,
    displayName?: string,
    profile?: { prefix?: string; firstName: string; lastName: string },
  ): Promise<boolean> => {
    const logger = createLogger("EnhancedAuthStore");

    set({ isLoading: true, error: null, authProvider: "firebase" });

    try {
      // Use firstName and lastName to construct displayName if not provided
      const userDisplayName =
        displayName ||
        (profile
          ? `${profile.firstName} ${profile.lastName}`.trim()
          : undefined);

      const firebaseUser = await registerWithEmail(
        email,
        password,
        userDisplayName,
      );

      // A new account is unverified, and the user callables refuse unverified
      // accounts. So registration makes no getUser/updateUser call here. The
      // entered names are held locally and applied after the first verified
      // load (see applyPendingProfile). The session comes from Firebase Auth.
      if (profile) {
        savePendingProfile(firebaseUser.uid, {
          prefix: profile.prefix || undefined,
          firstName: profile.firstName,
          lastName: profile.lastName,
        });
      }

      const fallbackUser = convertFirebaseUserToMSBUser(firebaseUser);
      const msbUser: User = profile
        ? {
            ...fallbackUser,
            prefix: profile.prefix || undefined,
            firstName: profile.firstName,
            lastName: profile.lastName,
          }
        : fallbackUser;

      const therapist = convertUserToTherapist(msbUser);

      // Get Firebase token. By this point the Auth account exists and the
      // verification email has been sent, so a token failure must not fail the
      // registration — that would strand the user with a working account while
      // telling them registration failed. onAuthStateChanged refreshes the token
      // shortly after, and getToken() re-fetches on demand.
      let token = "";
      try {
        token = await getIdToken();
      } catch (tokenError) {
        logger?.warn?.("Token fetch failed after registration — continuing", {
          error:
            tokenError instanceof Error ? tokenError.message : "Unknown error",
          component: "EnhancedAuthStore",
        });
      }

      // Create session with Firebase token
      const session: AuthSession = {
        user: msbUser,
        token: token,
        expiresAt: new Date(Date.now() + 60 * 60 * 1000), // 1 hour
      };

      set({
        firebaseUser,
        currentUser: msbUser,
        currentTherapist: therapist,
        session,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      logger?.info?.("User registered successfully via Firebase", {
        userId: firebaseUser.uid,
        component: "EnhancedAuthStore",
      });

      // Perform health check after successful authentication
      performPostLoginHealthCheck(logger);

      return true;
    } catch (error) {
      let errorMessage = "Firebase registration failed";

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({
        error: errorMessage,
        isLoading: false,
      });

      logger?.error?.("Firebase registration failed", {
        error: errorMessage,
        component: "EnhancedAuthStore",
      });

      return false;
    }
  },

  // Google login - Optimized with parallel operations and optimistic UI
  loginWithGoogle: async (): Promise<boolean> => {
    const logger = createLogger("EnhancedAuthStore");
    const startTime = Date.now();

    set({ isLoading: true, error: null, authProvider: "firebase" });

    try {
      // Enhanced diagnostic logging
      logger?.info?.("Starting Google login process", {
        environment: import.meta.env.MODE,
        domain: window.location.origin,
        userAgent: navigator.userAgent,
        component: "EnhancedAuthStore",
      });

      const firebaseUser = await loginWithGoogle();
      const duration = Date.now() - startTime;

      logger?.info?.("Firebase Google login successful", {
        component: "EnhancedAuthStore",
      });

      // OPTIMISTIC UI: Set user as logged in immediately with fallback data.
      // Guard against a sign-out that lands while this async continuation was
      // in flight; otherwise this would resurrect a signed-out user.
      const fallbackUser = convertFirebaseUserToMSBUser(firebaseUser);
      const fallbackTherapist = convertUserToTherapist(fallbackUser);

      if (auth.currentUser?.uid === firebaseUser.uid) {
        set({
          firebaseUser,
          currentUser: fallbackUser,
          currentTherapist: fallbackTherapist,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        logger?.warn?.("Discarding optimistic Google login write: user is no longer signed in", {
          userId: firebaseUser.uid,
          component: "EnhancedAuthStore",
        });
        set({ isLoading: false });
      }

      try {
        const creationTime = new Date(firebaseUser.metadata.creationTime || '').getTime();
        const lastSignInTime = new Date(firebaseUser.metadata.lastSignInTime || '').getTime();
        const isNewUser = Math.abs(creationTime - lastSignInTime) < 10000;

        analyticsService.trackEvent({
          name: "therapist_login",
          parameters: {
            method: "google",
            login_duration_ms: duration,
            user_type: "therapist",
            is_returning_user: !isNewUser,
          },
        });
      } catch {}

      logger?.info?.("User logged in with Google (optimistic UI)", {
        userId: firebaseUser.uid,
        component: "EnhancedAuthStore",
      });

      // PARALLEL OPERATIONS: Fetch real data and token simultaneously
      const [userResult, tokenResult] = await Promise.allSettled([
        fetchFirebaseUserFromDatabase(firebaseUser),
        getIdToken(),
      ]);

      // Process results and update with real data when available
      const msbUser =
        userResult.status === "fulfilled" && userResult.value
          ? userResult.value
          : fallbackUser;

      const token =
        tokenResult.status === "fulfilled" && tokenResult.value
          ? tokenResult.value
          : null;

      // Update with real data if we have a token. Guard against a sign-out (or
      // a switch to a different user) that landed while this fetch/token call
      // was in flight; an unconditional write here would resurrect a
      // signed-out user (formal finding 2).
      if (token && auth.currentUser?.uid === firebaseUser.uid) {
        const therapist = convertUserToTherapist(msbUser);
        const session: AuthSession = {
          user: msbUser,
          token: token,
          expiresAt: new Date(Date.now() + 60 * 60 * 1000), // 1 hour
        };

        set({
          currentUser: msbUser,
          currentTherapist: therapist,
          session,
        });

        logger?.info?.("User data updated with real Firestore data", {
          userId: firebaseUser.uid,
          hasRealData: userResult.status === "fulfilled",
          component: "EnhancedAuthStore",
        });
      } else if (!token) {
        logger?.warn?.("Failed to get Firebase token, using fallback data", {
          userId: firebaseUser.uid,
          component: "EnhancedAuthStore",
        });
      } else {
        logger?.warn?.("Discarding late Google login write: user is no longer signed in", {
          userId: firebaseUser.uid,
          component: "EnhancedAuthStore",
        });
      }

      // Perform health check after successful authentication
      performPostLoginHealthCheck(logger);

      return true;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage =
        error instanceof Error ? error.message : "Google login failed";
      const errorCode = "unknown";
      const errorDetails =
        error instanceof Error ? { name: error.name, stack: error.stack } : {};

      // Enhanced error logging for production debugging
      logger?.error?.("Google login failed", {
        error: errorMessage,
        errorCode,
        errorDetails,
        environment: import.meta.env.MODE,
        domain: window.location.origin,
        userAgent: navigator.userAgent,
        component: "EnhancedAuthStore",
      });

      set({
        error: errorMessage,
        isLoading: false,
      });

      try {
        analyticsService.trackEvent({
          name: "therapist_login_failed",
          parameters: {
            method: "google",
            error_code: errorCode,
            login_duration_ms: duration,
            user_type: "therapist",
          },
        });
      } catch {}

      return false;
    }
  },

  // Enhanced logout supporting both providers
  // NOTE: This now delegates to Firebase logout which triggers AuthContext
  logout: async (): Promise<void> => {
    const logger = createLogger("EnhancedAuthStore");
    const { currentUser } = get();
    const sessionDuration = analyticsService.getSessionDuration();

    try {
      // Track logout event
      try {
        analyticsService.trackEvent({
          name: "therapist_logout",
          parameters: {
            session_duration_ms: sessionDuration,
            reason: "user_initiated",
            user_type: "therapist",
          },
        });
      } catch {}

      logger?.info?.(
        "EnhancedAuthStore.logout() called - delegating to Firebase",
        {
          userId: currentUser?.id,
          component: "EnhancedAuthStore",
        },
      );

      // Clear cached therapist/client IDs so a subsequent user does not inherit
      // the previous session's identifiers.
      idConsistencyService.clearCache();

      // Just call Firebase logout - AuthContext will handle state clearing
      await firebaseLogout();

      // State will be cleared automatically via authContextBridge when
      // AuthContext's onAuthStateChanged fires with null
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Logout failed";

      logger?.error?.("Logout failed", {
        error: errorMessage,
        userId: currentUser?.id,
        component: "EnhancedAuthStore",
      });

      throw error;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Get current therapist
  getCurrentTherapist: (): Therapist | null => {
    const { currentUser } = get();
    return currentUser?.userType === "therapist"
      ? (currentUser as Therapist)
      : null;
  },

  // Get authentication token
  getToken: async (): Promise<string | null> => {
    const { firebaseUser } = get();

    // Get token from Firebase Auth directly
    if (firebaseUser) {
      try {
        return await firebaseUser.getIdToken();
      } catch (error) {
        logger.error("Failed to get Firebase token:", error);
        return null;
      }
    }

    return null;
  },

  // Check token expiration
  checkTokenExpiration: (): {
    expired: boolean;
    expiringSoon: boolean;
    minutesRemaining: number;
  } => {
    const { session } = get();

    if (!session?.expiresAt) {
      // No JWT session, check Firebase token via getToken
      return {
        expired: false,
        expiringSoon: false,
        minutesRemaining: Infinity,
      };
    }

    const now = Date.now();
    const expirationTime = new Date(session.expiresAt).getTime();
    const timeUntilExpiration = expirationTime - now;
    const minutesRemaining = Math.floor(timeUntilExpiration / (60 * 1000));
    const warningThreshold = 5 * 60 * 1000; // 5 minutes

    const expired = timeUntilExpiration <= 0;
    const expiringSoon = !expired && timeUntilExpiration <= warningThreshold;

    return { expired, expiringSoon, minutesRemaining };
  },

  // Check if token is expiring soon
  isTokenExpiringSoon: (): boolean => {
    const check = get().checkTokenExpiration();
    return check.expiringSoon;
  },

  // Initialize authentication state
  // NOTE: This method is now a no-op since AuthContext handles all initialization
  // The store syncs automatically via authContextBridge
  initialize: async (): Promise<void> => {
    const logger = createLogger("EnhancedAuthStore");

    logger?.info?.(
      "EnhancedAuthStore.initialize() called - delegating to AuthContext",
      {
        component: "EnhancedAuthStore",
      },
    );

    // Just mark as initialized - actual state comes from AuthContext via bridge
    set({ isLoading: false, isInitialized: true });

    return;

  },

  // Switch to Firebase authentication
  // NOTE: This method is now a no-op since AuthContext handles all Firebase auth
  switchToFirebase: async (): Promise<void> => {
    const logger = createLogger("EnhancedAuthStore");

    logger?.info?.(
      "switchToFirebase() called - AuthContext handles Firebase auth",
      {
        component: "EnhancedAuthStore",
      },
    );

    // AuthContext already uses Firebase - no action needed
    set({ authProvider: "firebase" });
  },

  // Switch to JWT authentication
  switchToJWT: (): void => {
    const logger = createLogger("EnhancedAuthStore");

    // Clean up Firebase listeners
    cleanupAuthStateListener();

    // Clear Firebase state
    set({
      firebaseUser: null,
      storedUserData: null,
      authProvider: "jwt",
    });

    logger?.info?.("Switched to JWT authentication", {
      component: "EnhancedAuthStore",
    });
  },
}));

// Subscribe to global auth state from AuthContext (primary source of truth)
subscribeToAuthState((globalState) => {
  useEnhancedAuthStore.setState({
    currentUser: globalState.currentUser,
    currentTherapist: globalState.currentTherapist,
    firebaseUser: globalState.firebaseUser,
    isAuthenticated: globalState.isAuthenticated,
    isLoading: globalState.loading,
    isInitialized: !globalState.loading,
    error: globalState.error,
    authProvider: globalState.firebaseUser ? "firebase" : null,
    session: globalState.currentUser
      ? {
          user: globalState.currentUser,
          token: "", // Token is managed by Firebase
          expiresAt: new Date(Date.now() + 3600000), // 1 hour
        }
      : null,
  });
});

// Initialize with current global state
const initialState = getGlobalAuthState();
useEnhancedAuthStore.setState({
  currentUser: initialState.currentUser,
  currentTherapist: initialState.currentTherapist,
  firebaseUser: initialState.firebaseUser,
  isAuthenticated: initialState.isAuthenticated,
  isLoading: initialState.loading,
  isInitialized: !initialState.loading,
  error: initialState.error,
  authProvider: initialState.firebaseUser ? "firebase" : null,
});

// Cleanup function for when the app unmounts
export const cleanupEnhancedAuth = (): void => {
  cleanupAuthStateListener();
};

// NOTE: Auto-initialization disabled - AuthContext handles all initialization
// The store now syncs automatically via authContextBridge
// if (typeof window !== "undefined") {
//   // Only initialize in browser environment
//   useEnhancedAuthStore.getState().initialize();
// }
